import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, NetworkError, setCsrfToken, setDeviceIdentity, setReachabilityHandler, setUnauthorizedHandler, type DeviceIdentity } from "./lib/api.js";
import { ENV, normalizeConfig } from "./lib/config.js";
import { db } from "./lib/db.js";
import { postDisplay } from "./lib/display.js";
import { checkVerifier, IdleTimer, makeVerifier, offlineUnlockRefusal, type Verifier } from "./lib/idle.js";
import { agentOnline, type SlipContext } from "./lib/print.js";
import { chainReport, checkMirror, prune } from "./lib/queue.js";
import { DEFAULT_TAX_LIMITS, type TaxLimits } from "./lib/rules.js";
import { SyncEngine, type SyncState } from "./lib/sync.js";
import type { PosConfig, SyncResult } from "./lib/types.js";
import type { OtpProof } from "@templeos/shared/otp-browser";

interface LoginInput { username: string; password: string; counterCode: string; takeover: boolean; totp?: string; recaptchaToken?: string; otp?: OtpProof; challenge?: { nonce: string; counter: string } }

export interface Me {
  id: string;
  username: string;
  fullName: string;
  isSuperuser: boolean;
  mustChangePassword: boolean;
  permissions: string[];
  roles: string[];
  counter: { id: string; code: string; name: string; kind: string } | null;
}

/** GET /api/core/shifts/current (foundation shiftSummary). */
export interface ShiftSummary {
  shift: { id: string; counter_id: string; counter_code: string; counter_name: string; user_id: string; opened_at: string; opening_float: number; status: string };
  byMode: { mode: string; receipts: number; amount: number }[];
  byKind: { kind: string; qty: number; amount: number }[];
  counts: { issued: number; cancelled: number };
  expectedCash: number;
}
/** GET /api/core/profile (signed in): the temple name for slips and the Income Tax limits the server enforces. */
export interface Profile { name: string; trust_name?: string | null; name_hi?: string | null; pan_required_above?: number; cash_limit_269st?: number }

const LOCK_KEY = "tos-pos-locked";
const PAPER_KEY = "tos-pos-paper";
export const COUNTER_KEY = "tos-pos-counter";
export const APP_VERSION = "1.1.0";
const CONFIG_REFRESH_MS = 5 * 60_000;
const HEARTBEAT_MS = 30_000;

/** localStorage can be blocked (private mode, policies); the POS must keep working without it. */
export const local = {
  get: (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string | null): void => {
    try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch { /* storage unavailable: the IndexedDB copy still holds the data */ }
  },
};

let currentCashier: string | null = null;
export const engine = new SyncEngine({
  db,
  cashier: () => currentCashier,
  isNetworkError: (e) => e instanceof NetworkError,
  // Short enough that a hung upload never holds the queue for long; the server is idempotent, so a resend is safe.
  upload: (sales) => api.post<{ results: SyncResult[] }>("/api/counters/sync", { sales }, 20_000).then((r) => r.results),
});

/**
 * This terminal's identity from the device agent (GET /identity), cached for offline restarts. Bound counters accept
 * only the terminal that presents it (SOW Modules 12 and 14).
 */
async function loadDeviceIdentity(): Promise<void> {
  try {
    const res = await fetch(`${ENV.agentUrl.replace(/\/$/, "")}/identity`, { signal: AbortSignal.timeout(1_500) });
    const id = (await res.json()) as { mac?: string | null; deviceToken?: string | null };
    const d: DeviceIdentity = { token: id.deviceToken ?? null, mac: id.mac ?? null };
    setDeviceIdentity(d);
    await db.setMeta("device", d);
  } catch {
    const cached = await db.getMeta<DeviceIdentity>("device");
    if (cached) setDeviceIdentity(cached);
  }
}

interface SessionValue {
  status: "loading" | "signedOut" | "ready";
  me: Me | null;
  reachable: boolean;
  config: PosConfig | null;
  configError: string | null;
  profile: Profile | null;
  limits: TaxLimits;
  shift: ShiftSummary | null | undefined;          // undefined while loading
  locked: boolean;
  sync: SyncState;
  tamper: string | null;
  printerReady: boolean | null;
  storageAtRisk: boolean;
  paperWidth: 42 | 32;
  slip: SlipContext;
  can: (...perms: string[]) => boolean;
  login: (p: LoginInput) => Promise<void>;
  refreshAfterPasswordChange: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  lock: () => void;
  unlock: (password: string) => Promise<boolean>;
  refreshShift: () => Promise<void>;
  reloadConfig: () => Promise<void>;
  setPaperWidth: (w: 42 | 32) => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function useSession(): SessionValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionValue["status"]>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [reachable, setReachable] = useState(true);
  const [config, setConfig] = useState<PosConfig | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [shift, setShift] = useState<ShiftSummary | null | undefined>(undefined);
  const [locked, setLocked] = useState(() => local.get(LOCK_KEY) === "1");
  const [sync, setSync] = useState<SyncState>(engine.state);
  const [tamper, setTamper] = useState<string | null>(null);
  const [printerReady, setPrinterReady] = useState<boolean | null>(null);
  const [storageAtRisk, setStorageAtRisk] = useState(false);
  const [paperWidth, setPaper] = useState<42 | 32>(() => (local.get(PAPER_KEY) === "32" ? 32 : 42));
  const meRef = useRef<Me | null>(null);
  const templeName = useRef("Temple"); // for the customer display, without re-running the effects that use it
  useEffect(() => { templeName.current = profile?.name ?? "Temple"; }, [profile]);

  const refreshShift = useCallback(async () => {
    const m = meRef.current;
    if (!m) return;
    const key = `shift:${m.username}`;
    try {
      const s = await api.get<ShiftSummary | null>("/api/core/shifts/current");
      setShift(s);
      await db.setMeta(key, s);
    } catch (e) {
      if (e instanceof NetworkError) setShift((await db.getMeta<ShiftSummary | null>(key)) ?? null);
      else {
        setConfigError(e instanceof Error ? e.message : "Could not read your shift.");
        setShift((cur) => (cur === undefined ? null : cur));
      }
    }
  }, []);

  /** Counter items, prices (with today's festival surge) and limits: at sign-in, every 5 minutes, and on reconnect. */
  const reloadConfig = useCallback(async () => {
    try {
      const at = new Date(); // before the request: a bill synced meanwhile is counted twice for the quota, never missed
      const raw = await api.get<Record<string, unknown>>("/api/counters/pos-config");
      const cfg = normalizeConfig(raw, at);
      setConfig(cfg);
      setConfigError(null);
      await db.setMeta("config", cfg);
    } catch (e) {
      const cached = await db.getMeta<PosConfig>("config");
      if (cached && cached.counter.code === meRef.current?.counter?.code) setConfig(cached);
      if (!(e instanceof NetworkError)) setConfigError(e instanceof Error ? e.message : "Counter configuration could not be loaded.");
    }
    try {
      const p = await api.get<Profile>("/api/core/profile");
      setProfile(p);
      await db.setMeta("profile", p);
    } catch {
      setProfile((await db.getMeta<Profile>("profile")) ?? null);
    }
  }, []);

  const becomeReady = useCallback(async (m: Me) => {
    meRef.current = m;
    currentCashier = m.username;
    setMe(m);
    await db.setMeta("me", m);
    setStatus("ready");
    const mirror = await checkMirror(db, { getItem: local.get, setItem: local.set });
    setTamper(mirror.ok ? null : mirror.message ?? "Local sales store failed its integrity check.");
    if (m.counter?.kind !== "gate") {
      await reloadConfig();
      await refreshShift();
    }
    await engine.refreshCount();
    void engine.kick();
    prune(db).catch((e: unknown) => console.error("Local store clean-up failed", e));
  }, [reloadConfig, refreshShift]);

  const clearSession = useCallback(() => {
    setCsrfToken(null);
    meRef.current = null;
    currentCashier = null;
    engine.stop();
    postDisplay({ type: "idle", templeName: templeName.current });
    setMe(null);
    setConfig(null);
    setShift(undefined);
    setLocked(false);
    local.set(LOCK_KEY, null);
    setStatus("signedOut");
  }, []);

  // Boot: a live session, or (offline) the last cashier behind the lock screen.
  useEffect(() => {
    setUnauthorizedHandler(() => { if (meRef.current) clearSession(); });
    setReachabilityHandler(setReachable);
    const unsub = engine.subscribe(setSync);
    // Back online: fresh prices before the next bill, then the waiting queue (the CSRF token is renewed on first use).
    const onOnline = () => {
      if (meRef.current && meRef.current.counter?.kind !== "gate") void reloadConfig();
      void engine.kick();
    };
    window.addEventListener("online", onOnline);
    void navigator.storage?.persist?.().then((ok) => setStorageAtRisk(!ok), () => setStorageAtRisk(true));
    void (async () => {
      await loadDeviceIdentity();
      try {
        const r = await api.get<{ user: Me; csrfToken: string }>("/api/auth/me");
        setCsrfToken(r.csrfToken);
        await becomeReady(r.user);
      } catch (e) {
        const cached = e instanceof NetworkError ? await db.getMeta<Me>("me") : undefined;
        if (!cached) { setStatus("signedOut"); return; }
        if (cached.counter?.kind !== "gate") { // a gate restarts straight into offline scanning
          setLocked(true);
          local.set(LOCK_KEY, "1");
        }
        await becomeReady(cached);
      }
    })();
    return () => {
      unsub();
      window.removeEventListener("online", onOnline);
      setUnauthorizedHandler(null);
      setReachabilityHandler(null);
    };
  }, [becomeReady, clearSession, reloadConfig]);

  const login = useCallback(async (p: LoginInput) => {
    const counterCode = p.counterCode.trim().toUpperCase();
    const r = await api.post<{ csrfToken: string }>("/api/auth/login", { username: p.username.trim(), password: p.password, counterCode, takeover: p.takeover, totp: p.totp || null, otp: p.otp, recaptchaToken: p.recaptchaToken, challenge: p.challenge }, 45_000);
    setCsrfToken(r.csrfToken);
    const meRes = await api.get<{ user: Me; csrfToken: string }>("/api/auth/me");
    setCsrfToken(meRes.csrfToken);
    await db.setMeta("verifier", await makeVerifier(meRes.user.username, p.password));
    await db.setMeta("unlockFailures", 0);
    local.set(COUNTER_KEY, counterCode);
    local.set(LOCK_KEY, null);
    setLocked(false);
    await becomeReady(meRes.user);
  }, [becomeReady]);

  const refreshAfterPasswordChange = useCallback(async (password: string) => {
    const result = await api.get<{ user: Me; csrfToken: string }>("/api/auth/me");
    setCsrfToken(result.csrfToken);
    await db.setMeta("verifier", await makeVerifier(result.user.username, password));
    await db.setMeta("unlockFailures", 0);
    await becomeReady(result.user);
  }, [becomeReady]);

  /** Always signs out on this terminal. If the server cannot be told, its session ends on the idle timeout. */
  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* offline or refused: the local sign-out below still happens, and the server session expires on its own */
    }
    await db.meta.bulkDelete(["me", "verifier"]);
    clearSession();
  }, [clearSession]);

  const lock = useCallback(() => {
    local.set(LOCK_KEY, "1");
    setLocked(true);
    postDisplay({ type: "idle", templeName: templeName.current });
  }, []);

  /**
   * Unlock with the cashier's password: checked by the server when reachable (and the session renewed), else offline
   * against the verifier, with the same five-failure lockout as the server and a 24-hour limit.
   */
  const unlock = useCallback(async (password: string) => {
    const m = meRef.current;
    if (!m) return false;
    const done = () => { local.set(LOCK_KEY, null); setLocked(false); return true; };
    try {
      const r = await api.post<{ csrfToken: string }>("/api/auth/login", { username: m.username, password, counterCode: m.counter?.code ?? null, takeover: true });
      setCsrfToken(r.csrfToken);
      await db.setMeta("verifier", await makeVerifier(m.username, password));
      await db.setMeta("unlockFailures", 0);
      if (m.counter?.kind !== "gate") void reloadConfig();
      void engine.kick();
      return done();
    } catch (e) {
      if (e instanceof ApiError && e.code !== "TOTP_REQUIRED") throw e;
      const v = await db.getMeta<Verifier>("verifier");
      if (!v || v.username !== m.username) throw new Error("The server is unreachable and this terminal has no offline unlock for you. Wait for the network.");
      const failures = (await db.getMeta<number>("unlockFailures")) ?? 0;
      const refusal = offlineUnlockRefusal(v, failures);
      if (refusal) throw new Error(refusal);
      if (await checkVerifier(v, password)) {
        await db.setMeta("unlockFailures", 0);
        return done();
      }
      await db.setMeta("unlockFailures", failures + 1);
      return false;
    }
  }, [reloadConfig]);

  // Auto-lock after 5 minutes without a key press or touch. Not at gates: a quiet gate must keep accepting scans
  // (a scan typed into a lock screen would count as a failed password).
  const isGate = me?.counter?.kind === "gate";
  useEffect(() => {
    if (status !== "ready" || locked || isGate) return;
    const timer = new IdleTimer();
    const touch = () => timer.touch();
    const events = ["pointerdown", "keydown", "touchstart", "wheel"] as const;
    for (const ev of events) window.addEventListener(ev, touch, { passive: true });
    const iv = setInterval(() => { if (timer.isIdle()) lock(); }, 5_000);
    return () => {
      for (const ev of events) window.removeEventListener(ev, touch);
      clearInterval(iv);
    };
  }, [status, locked, lock, isGate]);

  // Prices, surge rules and quotas change during the day (and at midnight): keep the counter's copy fresh.
  useEffect(() => {
    if (status !== "ready" || isGate) return;
    const iv = setInterval(() => void reloadConfig(), CONFIG_REFRESH_MS);
    return () => clearInterval(iv);
  }, [status, isGate, reloadConfig]);

  // Heartbeat for the live monitor (SOW Module 7), carrying the offline queue size; it also makes sure a waiting
  // queue always has an upload scheduled.
  useEffect(() => {
    if (status !== "ready") return;
    const beat = async () => {
      await engine.refreshCount();
      if (engine.state.pending) engine.ensureScheduled();
      try {
        await api.post("/api/counters/heartbeat", { pendingOffline: engine.state.pending, appVersion: APP_VERSION, chain: await chainReport(db).catch(() => undefined) }, 5_000);
      } catch {
        /* telemetry only: an unreachable terminal simply shows as offline on the monitor */
      }
    };
    void beat();
    const iv = setInterval(() => void beat(), HEARTBEAT_MS);
    return () => clearInterval(iv);
  }, [status]);

  // Printer (device agent) health, shown in the header.
  const printerUrl = config?.printerUrl ?? ENV.agentUrl;
  useEffect(() => {
    if (status !== "ready") return;
    let alive = true;
    const check = async () => { const ok = await agentOnline(printerUrl); if (alive) setPrinterReady(ok); };
    void check();
    const iv = setInterval(check, 30_000);
    return () => { alive = false; clearInterval(iv); };
  }, [status, printerUrl]);

  const setPaperWidth = useCallback((w: 42 | 32) => { local.set(PAPER_KEY, String(w)); setPaper(w); }, []);

  const value = useMemo<SessionValue>(() => ({
    status, me, reachable, config, configError, profile, shift, locked, sync, tamper, printerReady, storageAtRisk, paperWidth,
    limits: {
      panAbove: profile?.pan_required_above ?? DEFAULT_TAX_LIMITS.panAbove,
      cashLimit: profile?.cash_limit_269st ?? DEFAULT_TAX_LIMITS.cashLimit,
    },
    slip: {
      templeName: profile?.name ?? "Temple", trustName: profile?.trust_name ?? null, counter: me?.counter?.code ?? "",
      cashier: me?.fullName ?? "", paperWidth, webUrl: ENV.webUrl,
    },
    can: (...perms) => Boolean(me && (me.isSuperuser || perms.some((p) => me.permissions.includes(p)))),
    login, refreshAfterPasswordChange, logout, lock, unlock, refreshShift, reloadConfig, setPaperWidth,
  }), [status, me, reachable, config, configError, profile, shift, locked, sync, tamper, printerReady, storageAtRisk, paperWidth, login, refreshAfterPasswordChange, logout, lock, unlock, refreshShift, reloadConfig, setPaperWidth]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
